import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const euro = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const vuoto = (v) => (v == null ? '' : String(v));
const numero = (v) => (v === '' || v == null ? null : Number(v));

const Campo = ({ id, label, nota, valore, onChange }) => (
  <label className="block text-sm font-medium text-gray-700">
    {label}
    <Input type="number" step="0.01" min="0" value={valore} onChange={(e) => onChange(id, e.target.value)} className="mt-1 h-9" data-testid={`fatturato-${id}`} />
    {nota && <span className="block text-xs font-normal text-gray-500 mt-0.5">{nota}</span>}
  </label>
);

const iniziali = (dati) => {
  const food = dati?.conto?.food?.magazzino || {};
  const bev = dati?.conto?.beverage?.magazzino || {};
  const ereditato = (m) => (m.inizio_da === 'fine mese precedente' ? vuoto(m.inizio) : null);
  return {
    fatturato_food: vuoto(dati?.fatturato?.food || ''), fatturato_beverage: vuoto(dati?.fatturato?.beverage || ''),
    acquisti_extra: vuoto(dati?.acquisti?.extra_non_fatturati || ''),
    magazzino_food_inizio: vuoto(food.inizio), magazzino_food_fine: vuoto(food.fine),
    magazzino_bev_inizio: vuoto(bev.inizio), magazzino_bev_fine: vuoto(bev.fine),
    ereditato_food: ereditato(food), ereditato_bev: ereditato(bev),
  };
};

// l'inizio preso dalla chiusura del mese prima non si salva: segue le correzioni di quel mese
const inizio = (valore, ereditato) => (ereditato != null && valore === ereditato ? null : numero(valore));

export default function FatturatoMeseModal({ anno, mese, dati, headers, onClose, onSaved }) {
  const [f, setF] = useState(() => iniziali(dati));
  const [salvo, setSalvo] = useState(false);
  const cambia = (id, v) => setF((p) => ({ ...p, [id]: v }));
  const netto = (v) => euro((Number(v) || 0) / 1.1);

  const salva = async () => {
    setSalvo(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/gestione/ricette/dashboard/fatturato-mensile`, {
        anno, mese, fatturato_food: Number(f.fatturato_food) || 0, fatturato_beverage: Number(f.fatturato_beverage) || 0,
        acquisti_extra: Number(f.acquisti_extra) || 0, magazzino_food_inizio: inizio(f.magazzino_food_inizio, f.ereditato_food),
        magazzino_food_fine: numero(f.magazzino_food_fine), magazzino_bev_inizio: inizio(f.magazzino_bev_inizio, f.ereditato_bev),
        magazzino_bev_fine: numero(f.magazzino_bev_fine),
      }, { headers: headers() });
      toast.success('Dati del mese salvati');
      onSaved();
    } catch (e) {
      toast.error('Salvataggio non riuscito');
    } finally {
      setSalvo(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white" data-testid="fatturato-modal">
        <DialogHeader><DialogTitle className="text-gray-900">{MESI[mese - 1]} {anno}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <section className="grid grid-cols-2 gap-3">
            <Campo id="fatturato_food" label="Incassi cibo" nota={`IVA 10% inclusa · senza IVA ${netto(f.fatturato_food)}`} valore={f.fatturato_food} onChange={cambia} />
            <Campo id="fatturato_beverage" label="Incassi bevande" nota={`IVA 10% inclusa · senza IVA ${netto(f.fatturato_beverage)}`} valore={f.fatturato_beverage} onChange={cambia} />
          </section>
          <Campo id="acquisti_extra" label="Acquisti cibo senza fattura" nota="Mercato, contanti: importo senza IVA" valore={f.acquisti_extra} onChange={cambia} />
          <section className="rounded-xl bg-gray-50 p-3 space-y-3">
            <p className="text-xs text-gray-600">Valore della merce in magazzino (prezzo d'acquisto, senza IVA). Se lo lasci vuoto il food cost usa solo gli acquisti del mese.</p>
            <div className="grid grid-cols-2 gap-3">
              <Campo id="magazzino_food_inizio" label="Cibo a inizio mese" nota={f.ereditato_food != null && f.magazzino_food_inizio === f.ereditato_food ? 'dalla chiusura del mese prima' : ''} valore={f.magazzino_food_inizio} onChange={cambia} />
              <Campo id="magazzino_food_fine" label="Cibo a fine mese" valore={f.magazzino_food_fine} onChange={cambia} />
              <Campo id="magazzino_bev_inizio" label="Bevande a inizio mese" nota={f.ereditato_bev != null && f.magazzino_bev_inizio === f.ereditato_bev ? 'dalla chiusura del mese prima' : ''} valore={f.magazzino_bev_inizio} onChange={cambia} />
              <Campo id="magazzino_bev_fine" label="Bevande a fine mese" valore={f.magazzino_bev_fine} onChange={cambia} />
            </div>
          </section>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose} data-testid="fatturato-annulla">Annulla</Button>
            <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" onClick={salva} disabled={salvo} data-testid="fatturato-salva">
              {salvo ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salva
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
